import React, { useState } from 'react';
import { Button, Modal, ModalHeader, ModalBody, ModalFooter, Alert, TextInput, Label, Checkbox } from 'flowbite-react';
import { FiCopy, FiCheck, FiUserPlus } from 'react-icons/fi';
import * as sched from 'sched';
import { useAuth } from '../../hooks/useAuth';
import { useWorkspace } from '@/context/WorkspaceContext';
import { useAsync } from 'react-async-hook';

interface InviteUserModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const InviteUserModal: React.FC<InviteUserModalProps> = ({ isOpen, onClose }) => {
  const { user, token, client } = useAuth();
  const { workspaceId } = useWorkspace();

  const [isGenerating, setIsGenerating] = useState(false);
  const [inviteToken, setInviteToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [selectedPermissions, setSelectedPermissions] = useState<sched.WorkspacePermission['type'][]>(['Read', 'Write']);

  const workspaceResponse = useAsync(async () => {
    if (!user || !token || !client || !workspaceId) {
      return;
    }

    const workspaceResponse = await sched.WrappedApiClient.get_workspace(client, token, workspaceId);
    return workspaceResponse;
  }, [workspaceId, token]);

  const generateInviteLink = async (permissions: sched.WorkspacePermission['type'][]) => {
    if (!user || !token || !client || !workspaceId) {
      setError('Authentication required');
      return;
    }

    setIsGenerating(true);
    setError(null);

    try {
      const createTokenRequest: sched.CreateTokenRequest = {
        expiration: null, // No expiration for now
        authorization: {
          type: 'InviteToWorkspace',
          workspace_id: workspaceId,
          permissions: selectedPermissions.map(p => ({ type: p })),
        },
      };

      const tokenResponse = await sched.WrappedApiClient.create_token(
        client,
        token,
        createTokenRequest
      );

      setInviteToken(tokenResponse.id);
    } catch (err) {
      console.error('Failed to generate invite token:', err);
      setError('Failed to generate invite link. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const getInviteUrl = () => {
    if (!inviteToken) return '';
    const baseUrl = window.location.origin;
    return `${baseUrl}/invite/${inviteToken}`;
  };

  const copyToClipboard = async () => {
    const url = getInviteUrl();
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  };

  const handleClose = () => {
    setInviteToken(null);
    setError(null);
    setCopied(false);
    setSelectedPermissions(['Read', 'Write']); // Reset to default
    onClose();
  };

  const handlePermissionToggle = (permission: sched.WorkspacePermission['type']) => {
    setSelectedPermissions(prev =>
      prev.includes(permission)
        ? prev.filter(p => p !== permission)
        : [...prev, permission]
    );
  };

  const availablePermissions: sched.WorkspacePermission['type'][] = ['Read', 'Write', 'Share', 'Delete'];

  return (
    <Modal show={isOpen} onClose={handleClose} size="lg">
      <ModalHeader>
        <div className="flex items-center space-x-2">
          <FiUserPlus className="w-5 h-5" />
          <span>Invite User to {workspaceResponse?.result?.name || 'Workspace'}</span>
        </div>
      </ModalHeader>

      <ModalBody className="space-y-4">
        {error && (
          <Alert color="failure">
            {error}
          </Alert>
        )}

        {!inviteToken ? (
          <div className="text-center py-6">
            <FiUserPlus className="w-12 h-12 mx-auto text-cm-text-muted mb-4" />
            <h3 className="text-lg font-medium text-cm-text-primary mb-2">
              Generate Invitation Link
            </h3>
            <p className="text-cm-text-secondary mb-6">
              Create a secure invitation link to share with the person you want to invite to this workspace.
            </p>

            {/* Permissions Selector */}
            <div className="text-left mb-6">
              <Label className="text-sm font-medium text-cm-text-primary mb-3 block">
                Permissions
              </Label>
              <div className="grid grid-cols-2 gap-3">
                {availablePermissions.map((permission) => (
                  <div key={permission} className="flex items-center space-x-2">
                    <Checkbox
                      id={`permission-${permission.toLowerCase()}`}
                      checked={selectedPermissions.includes(permission)}
                      onChange={() => handlePermissionToggle(permission)}
                    />
                    <Label
                      htmlFor={`permission-${permission.toLowerCase()}`}
                      className="text-sm text-cm-text-primary cursor-pointer"
                    >
                      {permission}
                    </Label>
                  </div>
                ))}
              </div>
            </div>
            <Button
              onClick={() => generateInviteLink(selectedPermissions)}
              disabled={isGenerating}
              className="w-full"
            >
              {isGenerating ? 'Generating...' : 'Generate Invite Link'}
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <Label htmlFor="invite-url">Invitation Link</Label>
              <div className="flex space-x-2 mt-1">
                <TextInput
                  id="invite-url"
                  value={getInviteUrl()}
                  readOnly
                  className="flex-1"
                />
                <Button
                  color="gray"
                  onClick={copyToClipboard}
                  className="px-3"
                >
                  {copied ? <FiCheck className="w-4 h-4" /> : <FiCopy className="w-4 h-4" />}
                </Button>
              </div>
              {copied && (
                <p className="text-sm text-green-600 mt-1">
                  Link copied to clipboard!
                </p>
              )}
            </div>

            <Alert color="info">
              <div className="space-y-2">
                <p className="font-medium">How to use this invitation:</p>
                <ul className="list-disc list-inside space-y-1 text-sm">
                  <li>Share this link with the person you want to invite</li>
                  <li>They'll be prompted to sign in or create an account</li>
                  <li>After authentication, they'll automatically join this workspace</li>
                  <li>They can then set up their profile and start collaborating</li>
                </ul>
              </div>
            </Alert>
          </div>
        )}
      </ModalBody>

      <ModalFooter>
        <div className="flex flex-col space-y-2 w-full">
          <Button color="gray" onClick={handleClose} className="w-full !w-full block">
            {inviteToken ? 'Done' : 'Cancel'}
          </Button>
          {inviteToken && (
            <Button onClick={() => setInviteToken(null)} className="w-full !w-full block">
              Generate New Link
            </Button>
          )}
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default InviteUserModal;
